import { Injectable } from '@angular/core';

import { I18nService } from '@recruitee/i18n';

import type { PlaceholderData } from './placeholder';
import { Placeholder, PlaceholderCategory, PlaceholderId } from './placeholder';

export interface PlaceholderCollection {
  Basic: PlaceholderId[];
  Extended: PlaceholderId[];
}

export type PlaceholdersData = Partial<Record<PlaceholderId, PlaceholderData>>;

@Injectable({
  providedIn: 'root',
})
export class PlaceholderService {
  public static readonly Collection: PlaceholderCollection = {
    Basic: [
      PlaceholderId.FirstName,
      PlaceholderId.LastName,
      PlaceholderId.Candidate,
      PlaceholderId.JobOffer,
      PlaceholderId.Company,

      PlaceholderId.CandidateFirst,
      PlaceholderId.CandidateLast,
      PlaceholderId.CandidateFull,
      PlaceholderId.CandidateFullEmail,
    ],
    Extended: [
      PlaceholderId.FirstName,
      PlaceholderId.LastName,
      PlaceholderId.Candidate,
      PlaceholderId.EmailSender,
      PlaceholderId.JobOffer,
      PlaceholderId.Company,
      PlaceholderId.RecruiterPerJob,
      PlaceholderId.HiringManager,
      PlaceholderId.Department,

      PlaceholderId.CandidateFirst,
      PlaceholderId.CandidateLast,
      PlaceholderId.CandidateFull,
      PlaceholderId.CandidateFullEmail,

      PlaceholderId.EmailSenderFirst,
      PlaceholderId.EmailSenderLast,
      PlaceholderId.EmailSenderFull,
      PlaceholderId.EmailSenderFullEmail,

      PlaceholderId.RecruiterPerJobFirst,
      PlaceholderId.RecruiterPerJobLast,
      PlaceholderId.RecruiterPerJobFull,
      PlaceholderId.RecruiterPerJobFullEmail,

      PlaceholderId.HiringManagerFirst,
      PlaceholderId.HiringManagerLast,
      PlaceholderId.HiringManagerFull,
      PlaceholderId.HiringManagerFullEmail,
    ],
  };

  public deprecatedCollection = [
    PlaceholderId.FirstName,
    PlaceholderId.LastName,
    PlaceholderId.Candidate,
    PlaceholderId.EmailSender,
    PlaceholderId.RecruiterPerJob,
    PlaceholderId.HiringManager,
    PlaceholderId.EventTitle,
  ];

  public jobRelatedCollection = [
    PlaceholderId.RecruiterPerJobFirst,
    PlaceholderId.RecruiterPerJobLast,
    PlaceholderId.RecruiterPerJobFull,
    PlaceholderId.RecruiterPerJobFullEmail,

    PlaceholderId.HiringManagerFirst,
    PlaceholderId.HiringManagerLast,
    PlaceholderId.HiringManagerFull,
    PlaceholderId.HiringManagerFullEmail,

    PlaceholderId.RecruiterPerJob,
    PlaceholderId.HiringManager,
    PlaceholderId.Department,

    PlaceholderId.JobOffer,
  ];

  public placeholderCategoryTranslationMap: Record<string, { name: string; desc: string }> = {
    [PlaceholderCategory.Candidate]: {
      name: this.i18n.translate('app.email_templates.placeholder_categories.candidate'),
      desc: this.i18n.translate(
        'app.email_templates.placeholder_categories_descriptions.candidate',
      ),
    },
    [PlaceholderCategory.EmailSender]: {
      name: this.i18n.translate('app.email_templates.placeholder_categories.email_sender'),
      desc: this.i18n.translate(
        'app.email_templates.placeholder_categories_descriptions.email_sender',
      ),
    },
    [PlaceholderCategory.Event]: {
      name: this.i18n.translate('app.email_templates.placeholder_categories.event'),
      desc: this.i18n.translate('app.email_templates.placeholder_categories_descriptions.event'),
    },
    [PlaceholderCategory.HiringManager]: {
      name: this.i18n.translate('app.email_templates.placeholder_categories.hiring_manager'),
      desc: this.i18n.translate(
        'app.email_templates.placeholder_categories_descriptions.hiring_manager',
      ),
    },
    [PlaceholderCategory.Recruiter]: {
      name: this.i18n.translate('app.email_templates.placeholder_categories.recruiter_per_job'),
      desc: this.i18n.translate(
        'app.email_templates.placeholder_categories_descriptions.recruiter_per_job',
      ),
    },
  };

  private descriptions: Record<string, string> = {
    [PlaceholderId.FirstName]: this.i18n.translate('app.email_templates.first_name'),
    [PlaceholderId.LastName]: this.i18n.translate('app.email_templates.last_name'),
    [PlaceholderId.Candidate]: this.i18n.translate('app.email_templates.candidate'),
    [PlaceholderId.Company]: this.i18n.translate('app.email_templates.company'),
    [PlaceholderId.Recruiter]: this.i18n.translate('app.email_templates.recruiter'),
    [PlaceholderId.EmailSender]: this.i18n.translate('app.email_templates.email_sender_v2'),
    [PlaceholderId.RecruiterPerJob]: this.i18n.translate(
      'app.email_templates.recruiter_per_job_v2',
    ),
    [PlaceholderId.HiringManager]: this.i18n.translate('app.email_templates.hiring_manager_v2'),
    [PlaceholderId.JobOffer]: this.i18n.translate('app.email_templates.job_offer'),
    [PlaceholderId.Department]: this.i18n.translate('app.email_templates.department'),
    [PlaceholderId.EventTitle]: this.i18n.translate('app.email_templates.event_title_v2'),

    [PlaceholderId.CandidateFirst]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.candidate.first',
    ),
    [PlaceholderId.CandidateLast]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.candidate.last',
    ),
    [PlaceholderId.CandidateFull]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.candidate.full',
    ),
    [PlaceholderId.CandidateFullEmail]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.candidate.full_email',
    ),

    [PlaceholderId.JobOfferAll]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.job_offer_all',
    ),

    [PlaceholderId.RecruiterPerJobFirst]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.recruiter_per_job.first',
    ),
    [PlaceholderId.RecruiterPerJobLast]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.recruiter_per_job.last',
    ),
    [PlaceholderId.RecruiterPerJobFull]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.recruiter_per_job.full',
    ),
    [PlaceholderId.RecruiterPerJobFullEmail]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.recruiter_per_job.full_email',
    ),

    [PlaceholderId.HiringManagerFirst]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.hiring_manager.first',
    ),
    [PlaceholderId.HiringManagerLast]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.hiring_manager.last',
    ),
    [PlaceholderId.HiringManagerFull]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.hiring_manager.full',
    ),
    [PlaceholderId.HiringManagerFullEmail]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.hiring_manager.full_email',
    ),

    [PlaceholderId.EventFull]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.event.full',
    ),
    [PlaceholderId.EventLink]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.event.link',
    ),
    [PlaceholderId.EventLocation]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.event.location',
    ),
    [PlaceholderId.EventDate]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.event.date',
    ),
    [PlaceholderId.EventType]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.event.type',
    ),
    [PlaceholderId.EventInterviewers]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.event.interviewers',
    ),

    [PlaceholderId.EmailSenderFirst]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.email_sender.first',
    ),
    [PlaceholderId.EmailSenderLast]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.email_sender.last',
    ),
    [PlaceholderId.EmailSenderFull]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.email_sender.full',
    ),
    [PlaceholderId.EmailSenderFullEmail]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.email_sender.full_email',
    ),
  };

  constructor(private i18n: I18nService) {}

  public createCollection(
    collectionName: keyof PlaceholderCollection,
    data: PlaceholdersData = {},
  ): Placeholder[] {
    return PlaceholderService.Collection[collectionName].map(
      id =>
        new Placeholder(id, {
          value: null,
          description: this.descriptions[id],
          status: null,
          ...(data[id] || {}),
        }),
    );
  }

  public createCustomCollection(
    placeholderIds: PlaceholderId[],
    data: PlaceholdersData = {},
  ): Placeholder[] {
    return (placeholderIds || []).map(
      id =>
        new Placeholder(id, {
          value: id,
          description: this.descriptions[id],
          status: null,
          ...(data[id] || {}),
        }),
    );
  }

  public isCategoryVisible(placeholders: Placeholder[], category: string): boolean {
    return placeholders.some(placeholder => placeholder.id.includes(category));
  }

  public filterPlaceholders(
    placeholders: Placeholder[],
    placeholderId: PlaceholderId,
  ): Placeholder[] {
    const placeholder = placeholders.find(placeholder => placeholder.id === placeholderId);
    return placeholder ? [placeholder] : [];
  }
}
